'use client'

import { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { toast } from 'react-toastify'
import { MdArrowBack, MdSave, MdThermostat } from 'react-icons/md'
import {
    getStorageUnit,
    createStorageUnit,
    updateStorageUnit,
    CreateStorageUnitData,
    StorageUnitType,
    STORAGE_UNIT_TYPE_LABELS,
} from '@/lib/api/stock/temperature-api'

export default function StorageUnitFormPage() {
    const navigate = useNavigate()
    const { id } = useParams()
    const isEditing = !!id

    const [loading, setLoading] = useState(false)
    const [saving, setSaving] = useState(false)
    const [formData, setFormData] = useState<CreateStorageUnitData>({
        name: '',
        type: 'REFRIGERATOR',
        location: '',
        minTemp: 2,
        maxTemp: 8,
        criticalMinTemp: 0,
        criticalMaxTemp: 10,
        sensorId: '',
    })

    useEffect(() => {
        if (isEditing) {
            loadStorageUnit()
        }
    }, [id])

    const loadStorageUnit = async () => {
        setLoading(true)
        try {
            const response = await getStorageUnit(Number(id))
            if (response.success) {
                const unit = response.result
                setFormData({
                    name: unit.name,
                    type: unit.type,
                    location: unit.location || '',
                    minTemp: unit.minTemp,
                    maxTemp: unit.maxTemp,
                    criticalMinTemp: unit.criticalMinTemp,
                    criticalMaxTemp: unit.criticalMaxTemp,
                    sensorId: unit.sensorId || '',
                })
            } else {
                toast.error(response.error || 'Erro ao carregar unidade')
                navigate('/stock/temperature')
            }
        } catch (error) {
            toast.error('Erro ao carregar unidade')
            navigate('/stock/temperature')
        } finally {
            setLoading(false)
        }
    }

    const handleTypeChange = (type: StorageUnitType) => {
        let minTemp = 2,
            maxTemp = 8,
            criticalMinTemp = 0,
            criticalMaxTemp = 10

        switch (type) {
            case 'REFRIGERATOR':
                minTemp = 2
                maxTemp = 8
                criticalMinTemp = 0
                criticalMaxTemp = 10
                break
            case 'FREEZER':
                minTemp = -25
                maxTemp = -15
                criticalMinTemp = -30
                criticalMaxTemp = -10
                break
            case 'ULTRA_FREEZER':
                minTemp = -90
                maxTemp = -60
                criticalMinTemp = -95
                criticalMaxTemp = -55
                break
            case 'AMBIENT':
                minTemp = 15
                maxTemp = 30
                criticalMinTemp = 10
                criticalMaxTemp = 35
                break
        }

        setFormData({
            ...formData,
            type,
            minTemp,
            maxTemp,
            criticalMinTemp,
            criticalMaxTemp,
        })
    }

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault()

        if (!formData.name.trim()) {
            toast.error('Nome é obrigatório')
            return
        }

        if (formData.minTemp >= formData.maxTemp) {
            toast.error('Temperatura minima deve ser menor que a maxima')
            return
        }

        setSaving(true)
        try {
            const response = isEditing
                ? await updateStorageUnit(Number(id), formData)
                : await createStorageUnit(formData)

            if (response.success) {
                toast.success(
                    isEditing
                        ? 'Unidade atualizada com sucesso'
                        : 'Unidade criada com sucesso'
                )
                navigate('/stock/temperature')
            } else {
                toast.error(response.error || 'Erro ao salvar unidade')
            }
        } catch (error) {
            toast.error('Erro ao salvar unidade')
        } finally {
            setSaving(false)
        }
    }

    if (loading) {
        return (
            <div className="flex flex-col items-center justify-center min-h-[400px] gap-4">
                <div className="loading loading-spinner loading-lg text-secondary"></div>
                <p className="text-base-content/60 text-sm">Carregando...</p>
            </div>
        )
    }

    return (
        <div className="flex flex-col gap-6">
            <div className="flex items-center gap-4">
                <button
                    onClick={() => navigate('/stock/temperature')}
                    className="btn btn-ghost btn-sm"
                >
                    <MdArrowBack className="text-xl" />
                </button>
                <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-info to-info/70 flex items-center justify-center shadow-lg">
                        <MdThermostat className="text-2xl text-white" />
                    </div>
                    <div>
                        <h1 className="sm:text-lg lg:text-2xl font-bold text-base-content">
                            {isEditing
                                ? 'Editar Unidade de Armazenamento'
                                : 'Nova Unidade de Armazenamento'}
                        </h1>
                        <p className="text-sm text-base-content/60">
                            {isEditing
                                ? 'Atualize os dados da unidade'
                                : 'Cadastre uma nova unidade de armazenamento'}
                        </p>
                    </div>
                </div>
            </div>

            <form onSubmit={handleSubmit} className="flex flex-col gap-6">
                <div className="bg-base-100 rounded-xl border border-base-300/50 p-6">
                    <h2 className="text-lg font-semibold mb-4">
                        Informações Básicas
                    </h2>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="form-control w-full md:col-span-2">
                            <label className="label">
                                <span className="label-text font-medium">
                                    Nome <span className="text-error">*</span>
                                </span>
                            </label>
                            <input
                                type="text"
                                className="input input-bordered w-full focus:input-primary"
                                placeholder="Ex: Geladeira Vacinas 01"
                                value={formData.name}
                                onChange={e =>
                                    setFormData({
                                        ...formData,
                                        name: e.target.value,
                                    })
                                }
                                required
                            />
                        </div>
                        <div className="form-control w-full">
                            <label className="label">
                                <span className="label-text font-medium">
                                    Tipo <span className="text-error">*</span>
                                </span>
                            </label>
                            <select
                                className="select select-bordered w-full focus:select-primary"
                                value={formData.type}
                                onChange={e =>
                                    handleTypeChange(
                                        e.target.value as StorageUnitType
                                    )
                                }
                            >
                                {Object.entries(STORAGE_UNIT_TYPE_LABELS).map(
                                    ([key, label]) => (
                                        <option key={key} value={key}>
                                            {label}
                                        </option>
                                    )
                                )}
                            </select>
                        </div>
                        <div className="form-control w-full">
                            <label className="label">
                                <span className="label-text font-medium">
                                    Localização
                                </span>
                            </label>
                            <input
                                type="text"
                                className="input input-bordered w-full focus:input-primary"
                                placeholder="Ex: Sala de Vacinas"
                                value={formData.location}
                                onChange={e =>
                                    setFormData({
                                        ...formData,
                                        location: e.target.value,
                                    })
                                }
                            />
                        </div>
                    </div>
                </div>

                <div className="bg-base-100 rounded-xl border border-base-300/50 p-6">
                    <h2 className="text-lg font-semibold mb-4">
                        Faixa de Temperatura
                    </h2>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                        <div className="form-control w-full">
                            <label className="label">
                                <span className="label-text font-medium">
                                    Temperatura Minima (C){' '}
                                    <span className="text-error">*</span>
                                </span>
                            </label>
                            <input
                                type="number"
                                className="input input-bordered w-full focus:input-primary"
                                step="0.1"
                                value={formData.minTemp}
                                onChange={e =>
                                    setFormData({
                                        ...formData,
                                        minTemp: parseFloat(e.target.value) || 0,
                                    })
                                }
                                required
                            />
                        </div>
                        <div className="form-control w-full">
                            <label className="label">
                                <span className="label-text font-medium">
                                    Temperatura Maxima (C){' '}
                                    <span className="text-error">*</span>
                                </span>
                            </label>
                            <input
                                type="number"
                                className="input input-bordered w-full focus:input-primary"
                                step="0.1"
                                value={formData.maxTemp}
                                onChange={e =>
                                    setFormData({
                                        ...formData,
                                        maxTemp: parseFloat(e.target.value) || 0,
                                    })
                                }
                                required
                            />
                        </div>
                        <div className="form-control w-full">
                            <label className="label">
                                <span className="label-text font-medium">
                                    Critico Min (C)
                                </span>
                            </label>
                            <input
                                type="number"
                                className="input input-bordered w-full focus:input-primary"
                                step="0.1"
                                value={formData.criticalMinTemp}
                                onChange={e =>
                                    setFormData({
                                        ...formData,
                                        criticalMinTemp:
                                            parseFloat(e.target.value) || 0,
                                    })
                                }
                            />
                            <label className="label">
                                <span className="label-text-alt text-base-content/60">
                                    Alerta critico abaixo
                                </span>
                            </label>
                        </div>
                        <div className="form-control w-full">
                            <label className="label">
                                <span className="label-text font-medium">
                                    Critico Max (C)
                                </span>
                            </label>
                            <input
                                type="number"
                                className="input input-bordered w-full focus:input-primary"
                                step="0.1"
                                value={formData.criticalMaxTemp}
                                onChange={e =>
                                    setFormData({
                                        ...formData,
                                        criticalMaxTemp:
                                            parseFloat(e.target.value) || 0,
                                    })
                                }
                            />
                            <label className="label">
                                <span className="label-text-alt text-base-content/60">
                                    Alerta critico acima
                                </span>
                            </label>
                        </div>
                    </div>
                </div>

                <div className="bg-base-100 rounded-xl border border-base-300/50 p-6">
                    <h2 className="text-lg font-semibold mb-4">
                        Sensor IoT (Opcional)
                    </h2>
                    <div className="form-control w-full">
                        <label className="label">
                            <span className="label-text font-medium">
                                ID do Sensor
                            </span>
                        </label>
                        <input
                            type="text"
                            className="input input-bordered w-full focus:input-primary"
                            placeholder="Ex: SENSOR-001"
                            value={formData.sensorId}
                            onChange={e =>
                                setFormData({
                                    ...formData,
                                    sensorId: e.target.value,
                                })
                            }
                        />
                        <label className="label">
                            <span className="label-text-alt text-base-content/60">
                                Identificador único do sensor para integração
                                automática
                            </span>
                        </label>
                    </div>
                </div>

                <div className="flex justify-end gap-2">
                    <button
                        type="button"
                        onClick={() => navigate('/stock/temperature')}
                        className="btn btn-ghost"
                    >
                        Cancelar
                    </button>
                    <button
                        type="submit"
                        className="btn btn-secondary gap-2"
                        disabled={saving}
                    >
                        {saving ? (
                            <span className="loading loading-spinner loading-sm"></span>
                        ) : (
                            <MdSave className="text-xl" />
                        )}
                        {isEditing ? 'Atualizar' : 'Salvar'}
                    </button>
                </div>
            </form>
        </div>
    )
}
